/**
 * The Accounting sub-navigation, in one place. The sidebar renders it; a test
 * proves every href has a route file behind it.
 */
export interface AccountingNavItem {
  title: string
  href: string
}

export const ACCOUNTING_NAV: readonly AccountingNavItem[] = [
  { title: 'Home', href: '/accounting' },
  { title: 'Transactions', href: '/accounting/transactions' },
  { title: 'Reconciliation', href: '/accounting/reconciliation' },
  { title: 'General ledger', href: '/accounting/general-ledger' },
  { title: 'Connections', href: '/accounting/accounts' },
  { title: 'Contractors & vendors', href: '/accounting/contractors' },
  { title: 'Documents', href: '/accounting/documents' },
]

/** Pages that moved. Connections merged into Accounts; old links and bookmarks keep working. */
export const ACCOUNTING_LEGACY_REDIRECTS: Readonly<Record<string, string>> = {
  '/accounting/connections': '/accounting/accounts',
}

export const ACCOUNTS_PATH = '/accounting/accounts'
export const CONTRACTORS_PATH = '/accounting/contractors'
export const GENERAL_LEDGER_PATH = '/accounting/general-ledger'

/** Where a moved path now lives, or null when it did not move. A trailing slash is ignored. */
export function resolveLegacyAccountingPath(path: string): string | null {
  const clean = path.length > 1 ? path.replace(/\/+$/, '') : path
  return ACCOUNTING_LEGACY_REDIRECTS[clean] ?? null
}

const PERIODIC_REPORTS = ['balance_sheet', 'profit_and_loss', 'cash_flow'] as const
const LEDGER_KEYS = ['period', 'from', 'to'] as const
const PERIODIC_KEYS = ['year', 'grain'] as const

function carry(query: Record<string, unknown>, keys: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const key of keys) {
    const value = query[key]
    if (typeof value === 'string' && value !== '') out[key] = value
  }
  return out
}

/**
 * The old `/accounting/reports?report=…` page. The general ledger has its own page now, and the
 * three periodic reports are tabs on Home. Only the keys each destination reads travel along.
 */
export function resolveLegacyReportsLocation(query: Record<string, unknown>): {
  to: string
  search: Record<string, string>
} {
  const report = typeof query.report === 'string' ? query.report : ''
  if (report === 'general_ledger') return { to: GENERAL_LEDGER_PATH, search: carry(query, LEDGER_KEYS) }
  const tab = (PERIODIC_REPORTS as readonly string[]).includes(report) ? report : 'profit_and_loss'
  return { to: '/accounting', search: { tab, ...carry(query, PERIODIC_KEYS) } }
}
